/**
 * Safe transactional document opener and legacy migrator for Paper2Ink.
 * Spec reference: .hermes/plans/2026-09-16_195645-paper2ink-saas-spec.md (§5.5, §15 SAAS-207, SAAS-216).
 *
 * Invariants:
 * - Transactional: Malformed input NEVER corrupts or mutates the existing document state.
 * - Disallowed URL schemes (file://, http://, ftp://, relative paths) strictly rejected.
 * - Unknown or unsupported schema versions rejected.
 * - Validates output against CIM v1 schema contracts.
 */

import {
  validateCIMDocument,
  type CIMDocument,
  type CompoundFill,
  type CenterlineStroke,
  type InkObject,
} from '../editor/model.ts';

export class LegacyMigrationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'LegacyMigrationError';
  }
}

export interface OpenDocumentResult {
  document: CIMDocument;
  warnings: string[];
}

const ALLOWED_DATA_URL_PREFIXES = [
  'data:image/png;base64,',
  'data:image/jpeg;base64,',
  'data:image/webp;base64,',
];

export function openDocument(rawJson: string, _currentDocument?: CIMDocument): OpenDocumentResult {
  let parsed: unknown;
  try {
    parsed = JSON.parse(rawJson);
  } catch (err) {
    throw new LegacyMigrationError(`Invalid JSON content: ${err instanceof Error ? err.message : String(err)}`);
  }

  if (!parsed || typeof parsed !== 'object') {
    throw new LegacyMigrationError('Document payload must be a non-null JSON object');
  }

  const record = parsed as Record<string, unknown>;

  // Case 1: CIM v1 document
  if (record.schema_version === 'cim-1.0') {
    const doc = record as unknown as CIMDocument;
    validateCIMDocument(doc);
    return {
      document: structuredClone(doc),
      warnings: [],
    };
  }

  // Case 2: Legacy local-prototype-1 format
  if (record.version === 'local-prototype-1' || (!record.schema_version && Array.isArray(record.objects))) {
    return migrateLegacyDocument(record);
  }

  // Case 3: Unknown or future schema version
  throw new LegacyMigrationError(
    `Unsupported or unknown document schema version: '${record.schema_version ?? record.version ?? 'undefined'}'`
  );
}

function migrateLegacyDocument(legacy: Record<string, unknown>): OpenDocumentResult {
  const warnings: string[] = ['Document was safely migrated from legacy format to CIM v1.0.'];

  // Validate source if present
  if (typeof legacy.source === 'string') {
    const src = legacy.source.trim();
    if (src.startsWith('http://') || src.startsWith('https://') || src.startsWith('file://') || src.startsWith('ftp://') || src.includes('://')) {
      throw new LegacyMigrationError(`Field 'source' contains disallowed URL scheme: ${src.slice(0, 30)}`);
    }
    if (!ALLOWED_DATA_URL_PREFIXES.some((prefix) => src.startsWith(prefix))) {
      throw new LegacyMigrationError("Field 'source' contains disallowed URL scheme or MIME type; only data:image/(png|jpeg|webp);base64 is permitted");
    }
  }

  const width = typeof legacy.width === 'number' && Number.isFinite(legacy.width) && legacy.width > 0 ? legacy.width : 800;
  const height = typeof legacy.height === 'number' && Number.isFinite(legacy.height) && legacy.height > 0 ? legacy.height : 600;
  const docId = typeof legacy.id === 'string' && legacy.id.length > 0 ? legacy.id : `migrated-${Date.now()}`;
  const now = new Date().toISOString();

  const legacyObjects = Array.isArray(legacy.objects) ? legacy.objects : [];
  const objects: InkObject[] = [];

  for (let idx = 0; idx < legacyObjects.length; idx++) {
    const item = legacyObjects[idx];
    if (!item || typeof item !== 'object') continue;
    const obj = item as Record<string, unknown>;

    const id = typeof obj.id === 'string' ? obj.id : `obj-${idx + 1}`;
    const color = typeof obj.color === 'string' && /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(obj.color)
      ? obj.color
      : '#000000';
    const opacity = typeof obj.opacity === 'number' && obj.opacity >= 0 && obj.opacity <= 1 ? obj.opacity : 1.0;
    const transform: [number, number, number, number, number, number] = [1, 0, 0, 1, 0, 0];

    const type = String(obj.type ?? 'contour');

    if (type === 'stroke') {
      const rawPoints = Array.isArray(obj.points) ? obj.points : [];
      const points: [number, number][] = [];
      for (const pt of rawPoints) {
        if (Array.isArray(pt) && pt.length >= 2 && Number.isFinite(pt[0]) && Number.isFinite(pt[1])) {
          points.push([Number(pt[0]), Number(pt[1])]);
        }
      }
      if (points.length >= 2) {
        const strokeObj: CenterlineStroke = {
          id,
          kind: 'centerline_stroke',
          points,
          width: typeof obj.width === 'number' && obj.width > 0 ? obj.width : 1.5,
          color,
          opacity,
          transform,
          source_kind: 'reconstructed',
        };
        objects.push(strokeObj);
      }
    } else {
      // Default to contour / compound_fill
      const rawPoints = Array.isArray(obj.points) ? obj.points : [];
      const points: [number, number][] = [];
      for (const pt of rawPoints) {
        if (Array.isArray(pt) && pt.length >= 2 && Number.isFinite(pt[0]) && Number.isFinite(pt[1])) {
          points.push([Number(pt[0]), Number(pt[1])]);
        }
      }
      if (points.length >= 3) {
        const fillObj: CompoundFill = {
          id,
          kind: 'compound_fill',
          rings: [{ points }],
          color,
          opacity,
          fill_rule: 'evenodd',
          transform,
          source_kind: 'reconstructed',
        };
        objects.push(fillObj);
      }
    }
  }

  const migratedDoc: CIMDocument = {
    schema_version: 'cim-1.0',
    document_id: docId,
    revision_id: `rev-migrated-${Date.now()}`,
    parent_revision_id: null,
    page: {
      width,
      height,
      unit: 'px',
      space: 'processed-image',
    },
    source: {
      original_to_processed: [1, 0, 0, 0, 1, 0, 0, 0, 1],
      source_deleted: false,
      original_asset_id: null,
      normalized_asset_id: null,
      prepared_asset_id: null,
    },
    objects,
    provenance: {
      conversion_id: `conv-migrated-${Date.now()}`,
      model_version: 'legacy-import',
      model_manifest_hash: '0'.repeat(64),
      preprocess_version: 'legacy',
      vectorizer_version: 'legacy',
    },
    review: {
      requirement: 'source_comparison',
      assessment_kind: 'heuristic_uncalibrated',
      user_declaration: null,
    },
  };

  validateCIMDocument(migratedDoc);
  return {
    document: migratedDoc,
    warnings,
  };
}
